import {createContext, useContext, useEffect} from "react";

export type AccessEntry = {id: string; access: boolean};

/**
 * Lets whichever leaf component loaded the topic data report the backend's
 * `access` flag up to ContentPage, which owns the no-access gate. Reports are
 * tagged with the leaf's own resourceId, so a stale report from a previous
 * topic can never raise the gate on the next one. The context value is the
 * raw state setter from ContentPage (stable by React's contract); the no-op
 * default means a leaf rendered outside the provider reports into the void.
 */
export const AccessContext = createContext<(entry: AccessEntry) => void>(
    () => {}
);

/**
 * Report topic-level access to ContentPage. Pass `data?.access` directly:
 * undefined means "not loaded yet" and is never reported, so an in-flight
 * fetch neither raises nor lowers the gate.
 */
export const useReportAccess = (
    resourceId: string,
    access: boolean | undefined
) => {
    const setAccess = useContext(AccessContext);
    useEffect(() => {
        if (access !== undefined) setAccess({id: resourceId, access});
    }, [resourceId, access, setAccess]);
};
